import { IconButton, Snackbar, Alert } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  CardActionArea,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  Box as MuiBox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { StyledCard, CardContentBox, IconBox, AccentBar, pinShake, Ripple,pinPop } from "./style";
import { PushPin, PushPinOutlined, DragIndicator } from "@mui/icons-material";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import persistentStorageService from "../../../services/persistent-storage-service";
import { useUserRoles } from "../../../hooks/useUserRole";
import { ALL_FEATURES } from "../../../config/features";
import { hasPageAccess } from "../../../helpers/check-role-permission";

const MAX_PINS = 15;

const FeatureCardList = ({ features = [], onFeatureClick, isDraggable = false, onReorder, onPinChange }) => {
  const { roles } = useUserRoles();
  const theme = useTheme();
  const [pinnedIds, setPinnedIds] = useState([]);
  const isXXL = useMediaQuery("(min-width:2500px)");
  const isXL = useMediaQuery("(min-width:1920px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const [animateId, setAnimateId] = useState(null);
  const [disableRipple, setDisableRipple] = useState(false);
  const [showLimitWarning, setShowLimitWarning] = useState(false);

  const gradient = `linear-gradient(135deg, ${theme.palette.primary.main}, #42a5f5)`;
  const accentColor = theme.palette.primary.main;

  const xlSize = isXL ? 2.4 : 3;
  const size = isXXL ? 2 : xlSize;
  
  // Environment-specific localStorage key
  const environment = import.meta.env.VITE_ENVIRONMENT || 'Local';
  const storageKey = `pinnedFeatures_${environment}`;

  useEffect(() => {
    const storedValue = persistentStorageService.getItem(storageKey);
    const stored = storedValue ? JSON.parse(storedValue) : [];

    console.log('[FeatureCardList] Initial load:', { environment, storageKey, storedIds: stored });

    if(roles.length > 0){
      // Filter valid + accessible features
      const validPinnedIds = stored
        .filter(Boolean) // remove empty/invalid items
        .map(id => ALL_FEATURES.find(f => f.id === id)) // find feature details
        .filter(feature => feature && feature.route) // ensure route exists
        .filter(feature => hasPageAccess(roles, feature.route)) // check access
        .map(feature => feature.id); // convert back to IDs
      
      persistentStorageService.setItem(storageKey, JSON.stringify(validPinnedIds));
      setPinnedIds(validPinnedIds);
  
      // Notify parent component of pin change
      if (onPinChange) {
        onPinChange();
      }
    }
  }, [storageKey, environment, roles]);

  const togglePin = (e, featureId) => {
    setDisableRipple(true);
    if (!featureId) return;

    const isPinned = pinnedIds.includes(featureId);
    const updated = isPinned ? pinnedIds.filter((id) => id !== featureId) : [...pinnedIds, featureId];

    console.log('[FeatureCardList] Toggling pin:', {
      environment,
      storageKey,
      featureId,
      action: isPinned ? 'UNPIN' : 'PIN',
      updatedPinnedIds: updated
    });
    
    persistentStorageService.setItem(storageKey, JSON.stringify(updated));
    
    // Verify it was saved
    const verification = persistentStorageService.getItem(storageKey);
    console.log('[FeatureCardList] Saved to persistent storage:', verification);
    
    setPinnedIds(updated);
    setAnimateId(featureId);
    setTimeout(() => setAnimateId(null), 700);
    
    // Notify parent component of pin change
    if (onPinChange) {
      onPinChange();
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(features);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Call the onReorder callback to update parent state
    if (onReorder) {
      onReorder(items);
    } else {
      console.warn('FeatureCardList: onReorder prop is required when isDraggable is true');
    }
  };

  // Render card content
  const renderCard = (feature, index, isDragging = false, dragHandleProps = null) => {
    const isPinned = pinnedIds.includes(feature.id);
    const atPinLimit = !isPinned && pinnedIds.length >= MAX_PINS;

    return (
      <StyledCard 
        theme={theme} 
        isDark={isDark} 
        isMobile={isMobile} 
        sx={{ 
          position: "relative",
          transform: isDragging ? 'rotate(3deg)' : 'none',
          boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.3)' : undefined,
        }}
      >
        <CardActionArea
          disableRipple={disableRipple}
          onClick={() => onFeatureClick(feature.route)}
          sx={{ 
            height: "100%", 
            display: "flex", 
            flexDirection: "column", 
            justifyContent: "center", 
            p: { xs: 2, sm: 3 },
            border: isPinned ? '2px solid #1976d27e' : 'none' 
          }}
        >
          <CardContentBox sx={{ position: "relative" }}>
            {isDraggable && (
              <Tooltip title="Drag to reorder" placement="left" arrow>
                <MuiBox
                  {...dragHandleProps}
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    color: theme.palette.primary.main,
                    zIndex: 1,
                    cursor: "grab",
                    opacity: 0.6,
                    '&:hover': {
                      opacity: 1,
                    },
                    '&:active': {
                      cursor: 'grabbing',
                    }
                  }}
                >
                  <DragIndicator data-testid="DragIndicatorIcon" sx={{ fontSize: isMobile ? 28 : 32 }} />
                </MuiBox>
              </Tooltip>
            )}

            <Tooltip
              title={
                atPinLimit 
                  ? `Maximum ${MAX_PINS} pins reached. Unpin another item first.`
                  : isPinned
                  ? "Unpin this card from Home"
                  : "Pin this card to Home"
              }
              placement="top"
              arrow
            >
              <MuiBox
                onClick={(e) => {
                  // Always stop propagation to prevent navigation
                  e.stopPropagation();
                  e.preventDefault();
                  // Only toggle if not at limit or already pinned
                  if (!atPinLimit) {
                    togglePin(e, feature.id);
                  } else {
                    setShowLimitWarning(true);
                  }
                }}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  zIndex: 1,
                  cursor: atPinLimit ? 'not-allowed' : 'pointer',
                }}
              >
                <IconButton
                  disabled={atPinLimit}
                  aria-label={isPinned ? "Unpin this card from Home" : "Pin this card to Home"}
                  sx={{
                    color: isPinned 
                      ? theme.palette.primary.main 
                      : atPinLimit 
                      ? theme.palette.action.disabled
                      : theme.palette.text.secondary,
                    opacity: atPinLimit ? 0.4 : 1,
                    pointerEvents: 'none', // Prevent button from handling clicks
                  }}
                >
                  {isPinned ? (
                    <PushPin
                      sx={{
                        fontSize: isMobile ? 28 : 36,
                        transform: "rotate(45deg)",
                        animation: animateId === feature.id ? `${pinPop} 0.6s ease` : "none",
                      }}
                    />
                  ) : (
                    <PushPinOutlined
                      sx={{
                        fontSize: isMobile ? 28 : 36,
                        animation: animateId === feature.id ? `${pinShake} 0.6s ease` : "none",
                      }}
                    />
                  )} {animateId === feature.id && <Ripple isPinned={isPinned} theme={theme}/>}
                </IconButton>
              </MuiBox>
            </Tooltip>

            <IconBox gradient={gradient}>
              {React.cloneElement(feature.icon, { fontSize: isMobile ? "medium" : "large", sx: { color: "#fff" } })}
            </IconBox>

            <Typography
              variant="h6"
              title={feature.title}
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "1rem", sm: "1.25rem" },
                mb: 0.5,
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {feature.title}
            </Typography>

            <AccentBar color={accentColor} />

            <Tooltip
              title={
                <MuiBox sx={{ px: 2, py: 1, fontSize: "1rem", fontWeight: 600, color: theme.palette.primary.contrastText, textAlign: "center" }}>
                  {feature.description}
                </MuiBox>
              }
              arrow
              placement="bottom"
              enterDelay={300}
              leaveDelay={150}
              slotProps={{
                tooltip: {
                  sx: {
                    background: gradient,
                    color: theme.palette.primary.contrastText,
                    borderRadius: 2,
                    boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.3)",
                    border: `1px solid ${theme.palette.primary.light}`,
                  },
                },
                arrow: { sx: { color: theme.palette.primary.main } },
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  minHeight: "2.5em",
                }}
              >
                {feature.description}
              </Typography>
            </Tooltip>
          </CardContentBox>
        </CardActionArea>
      </StyledCard>
    );
  };

  if (!isDraggable) {
    // Regular non-draggable grid
    return (
      <>
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 1 }}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: size }} key={feature.id}>
              {renderCard(feature, index)}
            </Grid>
          ))}
        </Grid>

        {/* Pin limit warning */}
        <Snackbar
          open={showLimitWarning}
          autoHideDuration={4000}
          onClose={() => setShowLimitWarning(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setShowLimitWarning(false)} 
            severity="warning"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Maximum {MAX_PINS} pins reached. Unpin an item to add a new one.
          </Alert>
        </Snackbar>
      </>
    );
  }

  // Draggable grid
  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="features">
          {(provided, snapshot) => (
            <Grid 
              container 
              spacing={isMobile ? 2 : 3} 
              sx={{ mt: 1 }}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {features.map((feature, index) => (
                <Draggable key={feature.id} draggableId={feature.id} index={index}>
                  {(provided, snapshot) => (
                    <Grid
                      size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: size }}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      {renderCard(feature, index, snapshot.isDragging, provided.dragHandleProps)}
                    </Grid>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>

      {/* Pin limit warning */}
      <Snackbar
        open={showLimitWarning}
        autoHideDuration={4000}
        onClose={() => setShowLimitWarning(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowLimitWarning(false)} 
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Maximum {MAX_PINS} pins reached. Unpin an item to add a new one.
        </Alert>
      </Snackbar>
    </>
  );
};

FeatureCardList.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      route: PropTypes.string.isRequired,
    })
  ).isRequired,
  onFeatureClick: PropTypes.func.isRequired,
  isDraggable: PropTypes.bool,
  onReorder: function(props, propName, componentName) {
    if (props.isDraggable && !props[propName]) {
      return new Error(
        `${propName} is required when isDraggable is true in ${componentName}`
      );
    }
    if (props[propName] && typeof props[propName] !== 'function') {
      return new Error(
        `${propName} must be a function in ${componentName}`
      );
    }
  },
  onPinChange: PropTypes.func,
};

export default FeatureCardList;
